import React from 'react';
import { useIntl } from 'react-intl';
import { DashboardWidget } from './DashboardWidget';
import { getExecutionMismatch } from './executionMismatch';

interface ILastTrained {
  clientId: number;
  clientName: string;
  sessionDate: string;
  /** #6 ítem 3: cuándo entrenó de verdad. Null si no quedó registrado. */
  executedAt: string | null;
  sessionTime: string | null;
  durationMinutes: number | null;
  averageRpe: number | null;
  workoutName: string | null;
}

interface Props {
  data: ILastTrained[];
  loading: boolean;
  onClientClick?: (clientId: number) => void;
}

export const LastTrainedWidget: React.FC<Props> = ({ data, loading, onClientClick }) => {
  const intl = useIntl();

  const formatDuration = (minutes: number | null) => {
    if (minutes === null) return '-';
    if (minutes < 60) return `${minutes}min`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const formatDay = (day: Date | null) => {
    if (!day) return '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((today.getTime() - day.getTime()) / (1000 * 3600 * 24));

    if (diffDays === 0) return intl.formatMessage({ id: 'dashboard.today' });
    if (diffDays === 1) return intl.formatMessage({ id: 'dashboard.yesterday' });
    return day.toLocaleDateString(intl.locale, { day: 'numeric', month: 'short' });
  };

  return (
    <DashboardWidget
      title={intl.formatMessage({ id: 'dashboard.lastTrained' })}
      icon="pi pi-history"
      iconColor="#22c55e"
      loading={loading}
    >
      {data.length === 0 ? (
        <p style={{ color: 'var(--ios-text-tertiary)', fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0' }}>
          {intl.formatMessage({ id: 'dashboard.noTrainingData' })}
        </p>
      ) : (
        <div className="flex flex-column gap-2">
          {data.slice(0, 5).map((session, i) => (
            <div
              key={`${session.clientId}-${i}`}
              className="flex align-items-center gap-2"
              style={{
                padding: '0.5rem',
                borderRadius: 'var(--ios-radius-md)',
                background: 'var(--ios-surface-subtle)',
                cursor: onClientClick ? 'pointer' : 'default'
              }}
              onClick={() => onClientClick?.(session.clientId)}
            >
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--ios-text)' }}>
                  {session.clientName}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--ios-text-tertiary)' }}>
                  {session.workoutName ?? intl.formatMessage({ id: 'dashboard.session' })}
                </div>
              </div>
              <div className="flex align-items-center gap-2" style={{ flexShrink: 0 }}>
                {session.durationMinutes !== null && (
                  <span
                    style={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'var(--ios-text-secondary)',
                      background: 'var(--ios-surface-muted)',
                      padding: '0.15rem 0.4rem',
                      borderRadius: 'var(--ios-radius-pill)'
                    }}
                  >
                    {formatDuration(session.durationMinutes)}
                  </span>
                )}
                {/* #6 ítem 4: el RPE promedio se saca POR AHORA, no se borra —
                    Enzo quiere otro indicador en este lugar más adelante.
                {session.averageRpe !== null && (
                  <span
                    style={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      color: '#4d7c0f',
                      background: 'rgba(163, 230, 53, 0.18)',
                      padding: '0.15rem 0.4rem',
                      borderRadius: 'var(--ios-radius-pill)'
                    }}
                  >
                    RPE {session.averageRpe}
                  </span>
                )}
                */}
                {/* #6 ítem 3: se muestra la fecha en que ENTRENÓ, no la
                    programada. Si no coinciden, el triangulito lo aclara. */}
                {(() => {
                  const exec = getExecutionMismatch(session.sessionDate, session.executedAt);
                  const shown = exec.executed ?? exec.scheduled;
                  return (
                    <span
                      style={{
                        fontSize: '0.7rem',
                        color: 'var(--ios-text-tertiary)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}
                    >
                      {formatDay(shown)}
                      {exec.mismatch && (
                        <i
                          className="pi pi-exclamation-triangle"
                          style={{ fontSize: '0.65rem', color: '#f59e0b' }}
                          title={intl.formatMessage(
                            { id: 'dashboard.trainedOnAnotherDay' },
                            {
                              person: session.clientName,
                              day: formatDay(exec.executed),
                              scheduledDay: formatDay(exec.scheduled)
                            }
                          )}
                        />
                      )}
                    </span>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardWidget>
  );
};
